import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import api from "@/lib/api";

// Per-change access scope: empty = every change in the workspace (the default). Used by the invite form and the members list.
export function scopeLabel(ids) {
  const n = (ids || []).length;
  return n ? `${n} change${n === 1 ? "" : "s"}` : "All changes";
}

export function ScopeBadge({ ids, testid }) {
  const n = (ids || []).length;
  return <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${n ? "bg-warning-50 border-warning-200 text-warning-900" : "bg-secondary border-border text-subtle"}`}>{n ? <Lock size={10} /> : null}{scopeLabel(ids)}</span>;
}

export default function ScopePicker({ value, onChange, testid = "scope-picker", label = "Restrict to specific changes (optional)", open: forceOpen = false }) {
  const [changes, setChanges] = useState(null);
  const [open, setOpen] = useState((value || []).length > 0 || forceOpen);
  useEffect(() => { if (forceOpen) setOpen(true); }, [forceOpen]);
  useEffect(() => { if (open && !changes) api.get("/changes", { params: { limit: 200 } }).then((r) => setChanges(r.data.items.filter((c) => !c.is_example))).catch(() => setChanges([])); }, [open, changes]);
  const ids = value || [];
  const toggle = (id) => onChange(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  return (
    <div data-testid={testid} className="text-sm">
      <label className="flex items-center gap-2 text-xs font-semibold text-subtle">
        <input type="checkbox" data-testid={`${testid}-toggle`} checked={open} onChange={(e) => { setOpen(e.target.checked); if (!e.target.checked) onChange([]); }} className="h-4 w-4" /> {label}
      </label>
      {open && (
        <div className="mt-2 rounded-xl border border-border bg-card p-3 max-h-48 overflow-auto" role="group" aria-label="Changes this person can see">
          {changes === null && <div className="text-xs text-muted-foreground">Loading changes…</div>}
          {changes && changes.length === 0 && <div className="text-xs text-muted-foreground">No changes yet — invite them with full access and restrict later.</div>}
          {changes && changes.map((c) => (
            <label key={c.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer">
              <input type="checkbox" data-testid={`${testid}-change-${c.id}`} checked={ids.includes(c.id)} onChange={() => toggle(c.id)} className="h-4 w-4" />
              <span className="font-mono text-xs text-muted-foreground">{c.ref}</span><span className="truncate">{c.title}</span>
            </label>
          ))}
          <div className="text-[11px] text-muted-foreground mt-2">{ids.length ? `They will see only ${scopeLabel(ids)} — nothing else in this workspace, including counts.` : "Tick at least one change, or untick the box above for workspace-wide access."}</div>
        </div>
      )}
    </div>
  );
}
